import * as React from "react";
import {useEffect, useRef, useState} from "react";
import {translate} from "../../../translate/translate";
import {
	Box, Button, Checkbox,
	CircularProgress, Fab,
	FormControl, FormControlLabel,
	Grid, InputAdornment,
	InputLabel, ListItemText,
	MenuItem,
	Paper, Radio, RadioGroup, Switch, TextField,
	Typography
} from "@mui/material";
import TablePaginationComponent from "../../../components/table/table-pagination.component";
import {Add, Download, Search} from "@mui/icons-material";
import {URLS} from "../../../services/app-urls";
import {PaginatedResult} from "../../../models/default/paginated-result";
import {ErrorComponent} from "../../../components/error/error.component";
import {Client} from "../../../models/basic/client";
import {sortListObj} from "../../../utils/list-utils";
import {ErrorMessage, Field, Form, Formik, FormikHelpers} from "formik";
import Select from "@mui/material/Select";
import TableActionComponent from "../../../components/table/table-action.component";
import CollapsibleTableComponent from "../../../components/table/collapsible-table.component";
import {RelatedCost, RelatedCostForm} from "../../../models/basic/related-cost";
import {toast} from "react-toastify";
import {AbstractService} from "../../../services/abstract-service";
import {RelatedCostFilter, RelatedCostService} from "../../../services/related-cost-service";
import {useFilters} from "../../../filters/base-filter";
import {usePagination} from "../../../utils/pagination";
import {orderByFn} from "../../../utils/order-by";
import AddInvoiceComponent from "./add-invoice";


const RelatedCostComponent = () => {
	const service = new RelatedCostService();
	const serviceClient = new AbstractService<Client>(URLS.CLIENT);
 
	const {filters, updateFilterKeepingParam, updateFilter } = useFilters<RelatedCostFilter>(
    { client_id: "", client: "", ordering: "client,project", project: "", years: "" },
    ["client_id", "client", "ordering", "project", "years"]
	);
	const [relatedCostList, setRelatedCostList] = React.useState<PaginatedResult<RelatedCost>>();
  const [orderDirection, setOrderDirection] = React.useState<'asc' | 'desc' | ''>('asc');

  
	const {
    dataSource,
    pagination,
    resetPage,
    handleChangePage,
    handleChangeRowsPerPage
  } = usePagination<RelatedCost>(relatedCostList);
	const expand = [""];
 
	const [dataSourceClient, setDataSourceClient] = React.useState<Client[]>([]);
	
	const [loading, setLoading] = React.useState(false);
	
	const [dataSourceYears, setDataSourceYears] = React.useState<number[]>();
	const [selectedYears, setSelectedYears] = useState<any[]>([]);
  const [openDialog, setOpenDialog] = React.useState(false);

	const [loadingDownload, setLoadingDownload] = React.useState(false);

	
	useEffect(() => {
		loadClients();
		loadYears();
		initializeYears();
		search();
	}, []);

	useEffect(() => {
		if (dataSourceYears?.length) {
			const all = dataSourceYears.map((year: any) => year);
			setSelectedYears(all || []);
			updateFilterKeepingParam("years", all.join(","));
		}
	}, [dataSourceYears]);

	const initializeYears = () => {
    if (!filters.years) {
			return;
		}
		const years = filters.years?.split(",");
		setSelectedYears(years?.map(year => parseInt(year)) || []);
	}

	const loadClients = () => {
		serviceClient.readAll().then(response => {
			const sortedData = sortListObj(response.data, "name");
			setDataSourceClient(sortedData);
		}).catch((error: any) => {
			ErrorComponent(error);
		});
	}
 
	const loadYears = () => {
		service.listYears().then(response => {
			const sortedYears = response.data.years.sort((a: number, b: number) => b - a);
			setDataSourceYears(sortedYears);
		}).catch((error: any) => {
			ErrorComponent(error);
		});
	}
 
	const search = (restartIndex = false) => {
		setLoading(true);
		service.readPaginated(filters, expand, pagination).then(response => {
			setRelatedCostList(response.data);
		}).catch((error: any) => {
			ErrorComponent(error);
		}).finally(() => {
			setLoading(false);
		 })

		restartIndex && resetPage();

	}
	
	useEffect(() => {
		search();
	}, [pagination, filters.ordering]);

	const renderYears = (selected: any) => {
		if (allYearsSelected()) {
			return translate.t('all');
		}
		const sorted = selected.sort((a: number, b: number) => a - b);
	  	return sorted.join(", ");
	};
	
	const handleDelete = (row: RelatedCost) => {
		service.delete(Number(row.id)).then(() => {
			toast.success(translate.t("successfully_deleted"));
			search();
		}).catch((error: any) => {
			ErrorComponent(error);
		});
	};
	const handleOpenDialog = () => {
    	setOpenDialog(true);
	};
	
	const handleCloseDialog = (canSearch?: boolean) => {
		setOpenDialog(false);
		canSearch && search();
	};

	const layoutDetail = (row: any) => {
		return (
			<TablePaginationComponent
        key={row.id}
				state={row.items}
				displayedColumnsShow={["Date", "CY", "Document", "Type", "Category", "Material", "PartNumber/Description", "QTY", "Unit Net", "Taxes", "Total", "action"]}
				displayedColumns={["date", "cy", "invoice", "type_name", "category_name", "material", "description", "qty", "unit_net", "taxes", "total", "action"]}
				numberColumns={["unit_net", "taxes", "total"]}
				currencyColumns={["unit_net", "taxes", "total"]}
        columnStyles={{1:"10%", 2:"6%", 3:"10%", 4:"8%", 5:"6%", 6: "6%", 7:"20%", 8:"4%", 9: "8%", 10: "8%", 11: "8%", 12: "6%"}}
				dateColumns={["date"]}
				showActions={["delete"]}
				handleDelete={handleDelete}
			/>
		)
	}

	const export_xlsx = () => {
		search();
		setLoadingDownload(true);

		service.download(filters, 'export_xlsx').then(response => {
			service.downloadFile(response);
		}).catch((error: any) => {
			ErrorComponent(error);
		}).finally(() => {
			setLoadingDownload(false);
		});
	}

	const allYearsSelected = () => {
		return selectedYears.length === dataSourceYears?.length;
	}
	
	return (
		<section>
			<div className="floating-title" style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
				<Typography variant={"h5"}>
          			{translate.t("report_related_costs")}
				</Typography>
			</div>
			<div className="floating-button">
				<Button
					color="secondary"
					variant="contained"
					startIcon={<Add/>}
					onClick={() => {
            handleOpenDialog();
					}}>{translate.t("add_invoice_btn").toUpperCase()}</Button>
			</div>
			
			<Grid size={12}>
				<Paper variant="outlined">
					<Box p={2}>
						<Formik
							enableReinitialize={true}
							validateOnMount={true}
							initialValues={{name: ""}}
							onSubmit={(values, {setSubmitting}) => {
                setTimeout(() => {
                  search(true);
                  setSubmitting(false);
                }, 500);
							}}
						>
							{
								() => (
									<Form autoComplete="off">
										<Box alignItems="center" display="flex" justifyContent="space-between">
											<Grid alignItems="center" container spacing={2} direction={"row"} width="70%">
												<Grid>
												  <FormControl fullWidth>
													<InputLabel>
													  {translate.t("client")}
													</InputLabel>
													<Select
													  name="client"
													  label={translate.t("client")}
													  variant={"outlined"}
													  fullWidth
													  autoFocus={false}
													  value={filters.client_id || "0"}
													  onChange={(event) => updateFilterKeepingParam("client_id", event.target.value as string )}
													>
													  <MenuItem key="0" value="0">
														{translate.t('all')}
													  </MenuItem>
													  {dataSourceClient?.map((client: Client) => (
														<MenuItem key={client.id} value={client.id}>
														  {client.name}
														</MenuItem>
													  ))}
													</Select>
												  </FormControl>
												</Grid>
												<Grid size={4}>
													<FormControl fullWidth>
														<InputLabel>
														  	{translate.t("year")}
														</InputLabel>
														<Select
															name="year"
															label={translate.t("year")}
															variant={"outlined"}
															fullWidth
															autoFocus={false}
															value={selectedYears}
															onChange={(event: any) => {
																if (event.target.value.includes(0)) {
																	if (!allYearsSelected()) {
																		const years = dataSourceYears?.map((year: any) => year);
																		console.log(years);
																		setSelectedYears(years || []);
																		years?.length &&  updateFilterKeepingParam("years", years.join(","));
																		return;
																	}
																	setSelectedYears([]);
																	updateFilterKeepingParam("years", "");
																	return;
																}
																const validValues = event.target.value
																	.map((value: any) => Number(value))
																	.filter((value: number) => Number.isFinite(value));
																setSelectedYears(validValues);
																updateFilterKeepingParam("years", validValues.join(","));
															}}
															multiple
															renderValue={(selected) => renderYears(selected)}
															MenuProps={{
																PaperProps: {
																	style: {
																		maxHeight: '400px',
																	},
																},
																disableAutoFocusItem: true,
															}}
														>
															<MenuItem key={0} value={0}>
																<Checkbox checked={allYearsSelected()}/>
																<ListItemText primary={
																	<Box display="flex" alignItems="center" justifyContent="space-between">
																		<Typography variant="body1">{translate.t('all')}</Typography>
																		{allYearsSelected() && <Typography variant="body2"
																											  color="gray">{translate.t('clear')}</Typography>}
																	</Box>
																}/>
															</MenuItem>
															{dataSourceYears?.map((year: any) => (
																<MenuItem key={year} value={year}>
																	<Checkbox checked={selectedYears.includes(year)}/>
																	<ListItemText primary={year}/>
																</MenuItem>
															))}
														</Select>
													</FormControl>
												</Grid>
												<Grid>
													<Fab
														size="small"
														type="submit"
														color="secondary">
														<Search/>
													</Fab>
												</Grid>
											</Grid>
											<Grid alignItems="center" container justifyContent="flex-end" spacing={3}>
												<Grid>
													{loading && <CircularProgress size="30px" />}
												</Grid>
												<Grid>
													<Button
														disabled={loadingDownload}
														color="secondary"
														variant="contained"
														startIcon={<Download />}
														onClick={() => {
															export_xlsx();
														}}>{translate.t("export").toUpperCase()}
														{loadingDownload && <CircularProgress size="20px" sx={{marginLeft: '10px'}} />}
													</Button>
												</Grid>
											</Grid>
										</Box>
									</Form>
								)}
						</Formik>
					</Box>
				</Paper>
			</Grid>

			<Paper variant="outlined" sx={{marginTop: "16px"}}>
				<CollapsibleTableComponent
					state={dataSource}
					displayedColumnsShow={["Client", "Platform", "Total Value", "Total Amortized"]}
					displayedColumns={["client", "project", "total_nre", "total_amortized"]}
					numberColumns={["total_nre", "total_amortized"]}
					currencyColumns={["total_nre", "total_amortized"]}
					orderByColumns={["client", "project"]}
					orderBy={filters.ordering}
          orderByFn={(column: string, orderDirection: any) => {
            orderByFn(column, orderDirection, filters.ordering || '', setOrderDirection, updateFilterKeepingParam);
          }}
					orderDirection={orderDirection}
					actionsComponent={TableActionComponent}
					childrenComponent={layoutDetail}
					count={relatedCostList?.count}
					next={relatedCostList?.next}
					previous={relatedCostList?.previous}
					page={pagination.page}
					onPageChange={handleChangePage}
					rowsPerPage={pagination.limit}
					handleChangeRowsPerPage={handleChangeRowsPerPage}
					rowsPerPageOptions={[10, 50, 100]}
					loading={loading}
				/>
			</Paper>

			<AddInvoiceComponent
				open={openDialog}
				handleClose={handleCloseDialog}
			/>

		</section>
	);
};

export default RelatedCostComponent;
